import { CheckCircle } from '@mui/icons-material';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import DeleteOutlineTwoToneIcon from '@mui/icons-material/DeleteOutlineTwoTone';
import { Chip, IconButton, Skeleton, Typography, useTheme } from '@mui/material';
import { Box } from '@mui/material';
import { useState } from 'react';
import { useIntl } from 'react-intl';

import { Icon } from '@verantos-dev/shared-components';

import { AcceptedIcon, RejectedIcon, SupervisorIcon, SavedIcon } from './AnnotationIcons';
import { InProgressAnnotationCard } from './InProgressAnnotationCard';
import { SavedAnnotationCard } from './SavedAnnotationCard';
import { ShowEvidencesList } from './ShowEvidencesList';
import { useAppDispatch, useAppSelector } from '../../../../lib/store';
import { ConfirmDeleteModal } from '../../../workspace-common/components/ConfirmDeleteModal';
import type { Annotation } from '../../../workspace-common/types/responseTypes';
import { useDeleteAnnotationMutation } from '../../api/annotationApiSlice';
import {
  ANNOTATION_STATUS_ACCEPTED,
  ANNOTATION_STATUS_BOTH_ANNOTATORS,
  ANNOTATION_STATUS_ADDED_BY_SUPERVISOR,
  ANNOTATION_STATUS_CREATED,
  ANNOTATION_STATUS_DELETED,
  ANNOTATION_STATUS_REJECTED,
  ANNOTATION_STATUS_UPDATED,
} from '../../constants';
import {
  addToConceptAnnotationIdsInEditMode,
  clearHighlightedTextSpans,
  clearPendingSpansForAnnotation,
  removeFromConceptAnnotationIdsInEditMode,
  clearAddEvidenceModeForConceptId,
  clearCreateAnnotationError,
  setDeleteAnnotationError,
  clearDeleteAnnotationError,
} from '../../state/annotationSlice';
import { selectConceptAnnotationIdsInEditMode } from '../../state/selector';
import { selectDeleteAnnotationError } from '../../state/selector';

export const getStatusConfig = (status: string | undefined, theme: any) => {
  switch (status) {
    case ANNOTATION_STATUS_ACCEPTED:
      return {
        labelKey: 'annotationContent.annotationCard.accepted',
        chipBg: theme.palette.grey[300],
        chipLabelColor: theme.palette.common.black,
        borderColor: theme.palette.divider,
        bgColor: theme.palette.grey[100],
        icon: <AcceptedIcon width={24} height={24} style={{ color: theme.palette.success.main }} />,
      };

    case ANNOTATION_STATUS_REJECTED:
      return {
        labelKey: 'annotationContent.annotationCard.rejected',
        chipBg: theme.palette.grey[300],
        chipLabelColor: theme.palette.common.black,
        borderColor: theme.palette.divider,
        bgColor: theme.palette.grey[100],
        icon: <RejectedIcon width={20} height={20} style={{ color: theme.palette.error.main }} />,
      };

    case ANNOTATION_STATUS_DELETED:
      return {
        labelKey: 'annotationContent.annotationCard.deleted',
        chipBg: theme.palette.grey[300],
        chipLabelColor: theme.palette.common.black,
        borderColor: theme.palette.divider,
        bgColor: theme.palette.grey[100],
        icon: <Icon id="deleted-annotation-icon" icon="deleteDisabled" width={20} height={20} />,
      };

    case ANNOTATION_STATUS_ADDED_BY_SUPERVISOR:
      return {
        labelKey: 'annotationContent.annotationCard.addedBySupervisor',
        chipBg: theme.palette.grey[300],
        chipLabelColor: theme.palette.common.black,
        borderColor: theme.palette.divider,
        bgColor: theme.palette.grey[100],
        icon: <SupervisorIcon />,
      };

    case ANNOTATION_STATUS_BOTH_ANNOTATORS:
      return {
        labelKey: 'annotationContent.annotationCard.bothAnnotators',
        chipBg: theme.palette.grey[300],
        chipLabelColor: theme.palette.common.black,
        borderColor: theme.palette.divider,
        bgColor: theme.palette.grey[100],
        icon: <AcceptedIcon width={24} height={24} style={{ color: theme.palette.success.main }} />,
      };
    case ANNOTATION_STATUS_CREATED:
    case ANNOTATION_STATUS_UPDATED:
    default:
      return {
        labelKey: 'annotationContent.annotationCard.saved',
        chipBg: theme.palette.grey[300],
        chipLabelColor: theme.palette.common.black,
        borderColor: theme.palette.divider,
        bgColor: theme.palette.grey[100],
        icon: <SavedIcon width={20} height={20} style={{ color: theme.palette.success.main }} />,
      };
  }
};

interface AnnotationCardProps {
  annotationNumber: number;
  annotation: Annotation | null;
  conceptId: string;
}

export const AnnotationCard = ({
  annotationNumber,
  annotation,
  conceptId,
}: AnnotationCardProps) => {
  const theme = useTheme();
  const { formatMessage } = useIntl();
  const dispatch = useAppDispatch();
  const conceptAnnotationIdsInEditMode = useAppSelector(selectConceptAnnotationIdsInEditMode);
  const isInEditMode = annotation
    ? conceptAnnotationIdsInEditMode.includes(conceptId + annotation.annotationId)
    : false;
  const deleteAnnotationError = useAppSelector(
    selectDeleteAnnotationError(annotation?.annotationId ?? '')
  );
  const [deleteAnnotation, { isLoading: isDeleteAnnotationLoading }] =
    useDeleteAnnotationMutation();

  const [showDeleteConfirmationModal, setShowDeleteConfirmationModal] = useState<boolean>(false);

  const handleEditAnnotation = (e: React.MouseEvent<HTMLButtonElement>, annotationId: string) => {
    e.preventDefault();
    dispatch(clearDeleteAnnotationError());
    dispatch(addToConceptAnnotationIdsInEditMode(conceptId + annotationId));
  };

  const handleDeleteAnnotation = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    dispatch(clearDeleteAnnotationError());
    setShowDeleteConfirmationModal(true);
    return;
  };
  const handleConfirmDelete = async () => {
    if (!annotation?.annotationId) {
      return;
    }
    try {
      const result = await deleteAnnotation({ annotationId: annotation.annotationId }).unwrap();
      if (result?.data?.deleted === true) {
        dispatch(clearPendingSpansForAnnotation(annotation.annotationId));
        dispatch(clearHighlightedTextSpans());
        dispatch(clearAddEvidenceModeForConceptId());
        dispatch(removeFromConceptAnnotationIdsInEditMode(conceptId + annotation.annotationId));
        dispatch(clearCreateAnnotationError());
        dispatch(clearDeleteAnnotationError());
      }
    } catch (err: any) {
      const message =
        err?.data?.detail ||
        err?.data?.message ||
        err?.data?.errorMessage ||
        err?.message ||
        'Failed to delete annotation';
      dispatch(setDeleteAnnotationError({ annotationId: annotation.annotationId, error: message }));
    }
  };

  if (isDeleteAnnotationLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: theme.spacing(2),
          width: '100%',
          border: `1px solid ${theme.palette.divider}`,
          p: theme.spacing(4),
        }}
      >
        <Skeleton variant="text" width="100%" height="100px" />
      </Box>
    );
  }

  const statusConfig = isInEditMode
    ? {
        labelKey: 'annotationContent.annotationCard.inProgress',
        chipBg: theme.palette.grey[200],
        chipLabelColor: theme.palette.text.primary,
        borderColor: theme.palette.divider,
        bgColor: theme.palette.background.paper,
        icon: null,
      }
    : getStatusConfig(annotation?.status, theme);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(2),
        width: '100%',
        border: `1px solid ${statusConfig.borderColor}`,
        backgroundColor: statusConfig.bgColor,
        p: theme.spacing(4),
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          width: '100%',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: theme.spacing(1),
          }}
        >
          <Typography variant="body1" sx={{ fontWeight: '500' }}>
            Annotation #{annotationNumber}
          </Typography>
          {!isInEditMode && statusConfig.icon}

          <Chip
            label={formatMessage({
              id: statusConfig.labelKey,
            })}
            size="small"
            sx={{
              backgroundColor: statusConfig.chipBg,
              color: statusConfig.chipLabelColor,
              fontSize: '12px',
            }}
          />
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: theme.spacing(1) }}>
          {!isInEditMode && (
            <>
              <IconButton
                onClick={(e) => annotation && handleEditAnnotation(e, annotation.annotationId)}
              >
                <Icon icon="edit" id="edit-annotation-icon" width={20} height={20} />
              </IconButton>
              <IconButton disabled={!annotation?.annotationId} onClick={handleDeleteAnnotation}>
                <Icon
                  icon={!annotation?.annotationId ? 'deleteDisabled' : 'delete'}
                  id="delete-annotation-icon"
                  width={20}
                  height={20}
                />
              </IconButton>
            </>
          )}
        </Box>
      </Box>
      {isInEditMode ? (
        <>
          <InProgressAnnotationCard annotation={annotation} conceptId={conceptId} />
        </>
      ) : (
        <>
          {annotation && (
            <SavedAnnotationCard
              annotation={annotation}
              deleteError={deleteAnnotationError}
              onDismissDeleteError={() => dispatch(clearDeleteAnnotationError())}
            />
          )}
          {annotation && <ShowEvidencesList annotation={annotation} conceptId={conceptId} />}
        </>
      )}
      <ConfirmDeleteModal
        showModal={showDeleteConfirmationModal}
        setShowModal={setShowDeleteConfirmationModal}
        itemLabel={`Annotation ${annotationNumber}.`}
        onConfirm={handleConfirmDelete}
        isLoading={isDeleteAnnotationLoading}
        error={deleteAnnotationError}
      />
    </Box>
  );
};
